import { Component, signal, computed, ElementRef, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatChipsModule } from '@angular/material/chips';

export interface IconSize {
  label: string;
  width: number;
  height: number;
  description: string;
}

export const ICON_SIZES: IconSize[] = [
  // Android
  { label: '36×36 (Android ldpi)', width: 36, height: 36, description: 'Android LDPI' },
  { label: '48×48 (Android mdpi)', width: 48, height: 48, description: 'Android MDPI' },
  { label: '72×72 (Android hdpi)', width: 72, height: 72, description: 'Android HDPI' },
  { label: '96×96 (Android xhdpi)', width: 96, height: 96, description: 'Android XHDPI' },
  { label: '144×144 (Android xxhdpi)', width: 144, height: 144, description: 'Android XXHDPI' },
  { label: '192×192 (Android xxxhdpi / PWA)', width: 192, height: 192, description: 'Android XXXHDPI / PWA' },
  { label: '512×512 (Google Play)', width: 512, height: 512, description: 'Google Play Store' },
  // iOS
  { label: '20×20 (iOS Notification)', width: 20, height: 20, description: 'iOS Notification' },
  { label: '29×29 (iOS Settings)', width: 29, height: 29, description: 'iOS Settings' },
  { label: '40×40 (iOS Spotlight)', width: 40, height: 40, description: 'iOS Spotlight' },
  { label: '60×60 (iOS @1x)', width: 60, height: 60, description: 'iOS App Icon @1x' },
  { label: '76×76 (iPad @1x)', width: 76, height: 76, description: 'iPad App Icon @1x' },
  { label: '80×80 (iOS Spotlight @2x)', width: 80, height: 80, description: 'iOS Spotlight @2x' },
  { label: '87×87 (iOS Settings @3x)', width: 87, height: 87, description: 'iOS Settings @3x' },
  { label: '120×120 (iOS @2x)', width: 120, height: 120, description: 'iOS App Icon @2x' },
  { label: '152×152 (iPad @2x)', width: 152, height: 152, description: 'iPad App Icon @2x' },
  { label: '167×167 (iPad Pro)', width: 167, height: 167, description: 'iPad Pro App Icon' },
  { label: '180×180 (iOS @3x)', width: 180, height: 180, description: 'iOS App Icon @3x' },
  { label: '1024×1024 (App Store)', width: 1024, height: 1024, description: 'App Store' },
  // Web / Favicon
  { label: '16×16 (Favicon)', width: 16, height: 16, description: 'Browser Favicon' },
  { label: '32×32 (Favicon HiDPI)', width: 32, height: 32, description: 'Browser Favicon HiDPI' },
  { label: '128×128 (Web)', width: 128, height: 128, description: 'Web / PWA' },
  { label: '256×256 (Web/Desktop)', width: 256, height: 256, description: 'Web / Desktop' },
  // Windows
  { label: '44×44 (Windows small)', width: 44, height: 44, description: 'Windows Small Tile' },
  { label: '150×150 (Windows medium)', width: 150, height: 150, description: 'Windows Medium Tile' },
  { label: '310×150 (Windows wide)', width: 310, height: 150, description: 'Windows Wide Tile' },
  { label: '310×310 (Windows large)', width: 310, height: 310, description: 'Windows Large Tile' },
];

@Component({
  selector: 'app-app-icon-generator',
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatSelectModule,
    MatFormFieldModule,
    MatIconModule,
    MatCardModule,
    MatDividerModule,
    MatChipsModule,
  ],
  templateUrl: './app-icon-generator.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './app-icon-generator.css',
})
export class AppIconGenerator {
  readonly fileInput = viewChild<ElementRef<HTMLInputElement>>('fileInput');
  readonly canvas = viewChild<ElementRef<HTMLCanvasElement>>('canvas');

  iconSizes = ICON_SIZES;
  selectedSizes = signal<IconSize[]>([]);
  sourceImage = signal<HTMLImageElement | null>(null);
  previewUrl = signal<string>('');
  isDragOver = signal(false);
  generatedIcons = signal<{ size: IconSize; dataUrl: string }[]>([]);
  errorMsg = signal('');

  hasImage = computed(() => !!this.sourceImage());
  hasSelections = computed(() => this.selectedSizes().length > 0);
  canGenerate = computed(() => this.hasImage() && this.hasSelections());

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) this.loadFile(file);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver.set(true);
  }

  onDragLeave(): void {
    this.isDragOver.set(false);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver.set(false);
    const file = event.dataTransfer?.files?.[0];
    if (file) this.loadFile(file);
  }

  private loadFile(file: File): void {
    if (!file.type.startsWith('image/')) {
      this.errorMsg.set('請選擇圖片檔案');
      return;
    }
    this.errorMsg.set('');
    const reader = new FileReader();
    reader.onload = (e) => {
      const url = e.target?.result as string;
      const img = new Image();
      img.onload = () => {
        this.sourceImage.set(img);
        this.previewUrl.set(url);
        this.generatedIcons.set([]);
        if (img.width !== 1024 || img.height !== 1024) {
          this.errorMsg.set(
            `⚠️ 建議使用 1024×1024 圖片，目前為 ${img.width}×${img.height}，可能影響品質`
          );
        }
      };
      img.src = url;
    };
    reader.readAsDataURL(file);
  }

  toggleSize(size: IconSize): void {
    const current = this.selectedSizes();
    const idx = current.findIndex(s => s.label === size.label);
    if (idx >= 0) {
      this.selectedSizes.set(current.filter((_, i) => i !== idx));
    } else {
      this.selectedSizes.set([...current, size]);
    }
  }

  isSelected(size: IconSize): boolean {
    return this.selectedSizes().some(s => s.label === size.label);
  }

  selectAll(): void {
    this.selectedSizes.set([...this.iconSizes]);
  }

  clearAll(): void {
    this.selectedSizes.set([]);
  }

  generateIcons(): void {
    const img = this.sourceImage();
    const canvasEl = this.canvas()?.nativeElement;
    if (!img || !canvasEl) return;

    const results: { size: IconSize; dataUrl: string }[] = [];

    for (const size of this.selectedSizes()) {
      canvasEl.width = size.width;
      canvasEl.height = size.height;
      const ctx = canvasEl.getContext('2d')!;
      ctx.clearRect(0, 0, size.width, size.height);
      ctx.drawImage(img, 0, 0, size.width, size.height);
      const dataUrl = canvasEl.toDataURL('image/png');
      results.push({ size, dataUrl });
    }

    // Sort by area descending
    results.sort((a, b) => b.size.width * b.size.height - a.size.width * a.size.height);
    this.generatedIcons.set(results);
  }

  downloadIcon(icon: { size: IconSize; dataUrl: string }): void {
    const a = document.createElement('a');
    a.href = icon.dataUrl;
    a.download = `icon-${icon.size.width}x${icon.size.height}.png`;
    a.click();
  }

  downloadAll(): void {
    for (const icon of this.generatedIcons()) {
      setTimeout(() => this.downloadIcon(icon), 100);
    }
  }

  triggerFileInput(): void {
    this.fileInput()?.nativeElement.click();
  }
}
